<template>
  <div class="h-full flex flex-col p-6 space-y-6">
    <DateSelector v-model="selectedDate" @dateSelected="selectDate" />
    <!-- Top: KTB Type Selection -->
    <div class="bg-white rounded-lg shadow p-4">
      <div class="grid grid-cols-6 gap-2">
        <button v-for="type in ktbTypes" :key="type.id" @click="selectKtbType(type)" :class="[
          'px-2.5 py-1.5 rounded text-sm font-medium transition-colors',
          selectedType === type.id
            ? 'bg-gray-800 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        ]">
          {{ type.name }}
        </button>
      </div>
    </div>

    <!-- Bottom: Content Area -->
    <div class="flex flex-1 gap-6 h-[calc(100vh-12rem)]">
      <!-- Left: Transaction List -->
      <div class="w-[350px] bg-white rounded-lg shadow flex flex-col">
        <!-- Header -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex justify-between items-center">
            <h2 class="text-sm font-medium text-gray-700">거래 내역</h2>
            <div class="flex items-center gap-4">
              <button @click="refreshData" class="text-gray-600 hover:text-gray-900" :disabled="isLoading">
                <div class="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="{ 'animate-spin': isLoading }"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </button>
              <div class="text-sm text-gray-600">
                {{ selectedDate }}
              </div>
            </div>
          </div>
        </div>

        <!-- Transaction List -->
        <div class="flex-1 overflow-hidden">
          <div v-if="isLoading" class="text-center py-4">
            <div
              class="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-gray-400 rounded-full">
            </div>
            <div class="mt-2 text-sm text-gray-500">
              데이터를 불러오는 중...
            </div>
          </div>
          <div v-else-if="errorMessage" class="text-center py-4 text-red-600">
            {{ errorMessage }}
          </div>
          <div v-else-if="!selectedType" class="text-center py-4 text-gray-500">
            만기를 선택해주세요
          </div>
          <div v-else class="h-full overflow-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="sticky top-0 bg-white shadow-sm">
                <tr>
                  <th scope="col"
                    class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                    시간
                  </th>
                  <th scope="col"
                    class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                    스프레드
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="transaction in sortedTransactions" :key="transaction.id" :class="[
                  'transition-colors duration-150 ease-in-out hover:bg-opacity-75',
                  parseFloat(transaction.spreadBp) < 0
                    ? 'bg-red-50'
                    : 'bg-blue-50',
                ]">
                  <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {{ transaction.time }}
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right tabular-nums">
                    {{ transaction.spreadBp }}bp
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right: Chart -->
      <div class="flex-1 bg-white rounded-lg shadow p-6">
        <h2 class="text-sm font-medium text-gray-700 mb-4">
          일중 스프레드 추이
        </h2>
        <div class="h-[calc(100%-2rem)]">
          <KtbYieldBandChart v-if="selectedType && transactions.length > 0" :transactions="transactions" />
          <div v-else class="flex items-center justify-center h-full text-gray-500">
            {{ selectedType ? '데이터가 없습니다' : '만기를 선택해주세요' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { getTodayStr } from '@/utils/util';
import ktbService from '@/service/ktbService';
import KtbYieldBandChart from '@/components/ktb/KtbYieldBandChart.vue';
import DateSelector from '@/components/NavigationDateSelector.vue';

export default {
  name: 'KtbView',
  components: {
    KtbYieldBandChart,
    DateSelector,
  },
  setup() {
    const selectedDate = ref(getTodayStr());
    const selectedType = ref('');
    const transactions = ref([]);
    const isLoading = ref(false);
    const errorMessage = ref('');

    const ktbTypes = [
      { id: 'Y02', name: '2년' },
      { id: 'Y03', name: '3년' },
      { id: 'Y05', name: '5년' },
      { id: 'Y10', name: '10년' },
      { id: 'Y20', name: '20년' },
      { id: 'Y30', name: '30년' },
    ];

    const fetchData = async () => {
      if (!selectedType.value) return;

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const data = await ktbService.findDaily(
          selectedType.value,
          selectedDate.value
        );
        transactions.value = data;
      } catch (error) {
        console.error('Error fetching KTB data:', error);
        errorMessage.value = '데이터 조회 중 오류가 발생했습니다.';
        transactions.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const selectKtbType = async type => {
      selectedType.value = type.id;
      await fetchData();
    };

    const refreshData = () => {
      if (isLoading.value) return;
      fetchData();
    };

    // 시간 역순으로 정렬되고 가격 변화가 있는 거래 내역만 필터링
    const sortedTransactions = computed(() => {
      if (!transactions.value.length) return [];

      // 먼저 시간순으로 정렬 (오름차순)
      const timeSorted = [...transactions.value].sort((a, b) => {
        const timeA = a.time.split(':').map(Number);
        const timeB = b.time.split(':').map(Number);

        const secondsA = timeA[0] * 3600 + timeA[1] * 60 + (timeA[2] || 0);
        const secondsB = timeB[0] * 3600 + timeB[1] * 60 + (timeB[2] || 0);

        return secondsA - secondsB;
      });

      // 가격 변화가 있는 데이터만 필터링
      const filtered = timeSorted.filter((transaction, index) => {
        if (index === timeSorted.length - 1) return true; // 가장 최근 데이터는 항상 포함
        if (index === 0) return true; // 첫 데이터는 항상 포함

        // 이전 거래와 스프레드가 다른 경우만 포함
        const prevSpread = parseFloat(timeSorted[index - 1].spreadBp);
        const currentSpread = parseFloat(transaction.spreadBp);
        return prevSpread !== currentSpread;
      });

      // 최종적으로 시간 역순으로 정렬하여 반환
      return filtered.reverse();
    });

    // 날짜 선택 핸들러
    const selectDate = async date => {
      selectedDate.value = date;
      if (selectedType.value) {
        await fetchData();
      }
    };

    return {
      ktbTypes,
      selectedType,
      transactions,
      isLoading,
      errorMessage,
      selectedDate,
      selectKtbType,
      refreshData,
      sortedTransactions,
      selectDate,
    };
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
